/**
 * Reminders settings card — slot into Settings page.
 *
 * - Pick which appointment types get auto-reminders by default
 * - Choose 24h / 2h slot toggles
 * - Edit the reminder template body
 * - Admin email for SMS-reply notifications
 * - Inbound webhook URL to paste into ClickSend
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Bell, Save, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/intl";

export default function RemindersSettings() {
  const [cfg, setCfg] = useState(null);
  const [types, setTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [body, setBody] = useState("");
  const [send48, setSend48] = useState(false);
  const [send24, setSend24] = useState(true);
  const [send2, setSend2] = useState(true);
  const [autoCancel, setAutoCancel] = useState(true);
  const [enabledIds, setEnabledIds] = useState(new Set());
  const [copied, setCopied] = useState(false);

  async function refresh() {
    // Two independent calls — appointment-types can fail (e.g. Acuity 401)
    // without losing the reminders config that came back fine.
    let configErr = null;
    try {
      const c = await api.get("/reminders/config");
      setCfg(c.data);
      setAdminEmail(c.data.admin_email || "");
      setBody(c.data.template_body || "");
      setSend48(!!c.data.send_48h);
      setSend24(!!c.data.send_24h);
      setSend2(!!c.data.send_2h);
      setAutoCancel(c.data.auto_cancel_on_no !== false);
      setEnabledIds(new Set((c.data.enabled_appointment_type_ids || []).map((x) => Number(x))));
    } catch (e) {
      configErr = e?.response?.data?.detail || e.message;
    }
    try {
      const t = await api.get("/appointment-types");
      setTypes(t.data || []);
    } catch (e) {
      // Acuity may be unconfigured / 401 — that's fine, types list shows
      // a helpful empty state. We still allow config saves.
      setTypes([]);
    }
    if (configErr) {
      toast.error("Could not load reminder settings", { description: configErr });
    }
  }
  useEffect(() => { refresh(); }, []);

  async function save() {
    setSaving(true);
    try {
      await api.post("/reminders/config", {
        enabled_appointment_type_ids: Array.from(enabledIds),
        template_body: body,
        admin_email: adminEmail || null,
        send_48h: send48,
        send_24h: send24,
        send_2h: send2,
        auto_cancel_on_no: autoCancel,
      });
      toast.success("Reminder settings saved");
      await refresh();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  function toggleType(id) {
    setEnabledIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const webhookUrl = cfg?.webhook_token
    ? `${apiBase}/api/sms/webhook/inbound/${cfg.webhook_token}`
    : "";

  // Inbound diagnostics — pull the current inbound count so we can warn the
  // operator when zero replies have ever arrived (a sign their ClickSend
  // webhook isn't wired up).
  const [inboundCount, setInboundCount] = useState(null);
  const [selfTesting, setSelfTesting] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/sms/inbound?limit=1");
        setInboundCount(r.data?.total ?? (r.data?.rows?.length ?? 0));
      } catch {
        setInboundCount(null);
      }
    })();
  }, [cfg?.webhook_token]);

  async function copyWebhook() {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Webhook URL copied");
    } catch {
      toast.error("Copy failed");
    }
  }

  async function sendSelfTest() {
    if (!webhookUrl) return;
    setSelfTesting(true);
    try {
      // POST to our own webhook URL — proves the endpoint is publicly reachable.
      // Uses a distinct phone so the row is easy to spot in the inbox.
      const now = Date.now().toString().slice(-8);
      const r = await fetch(webhookUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          from: "+61400000000",
          body: `SELF-TEST ${now}`,
        }),
      });
      if (r.ok) {
        toast.success("Webhook reachable ✓", {
          description: "Look for 'SELF-TEST' in the Messaging inbox. If it's there, ClickSend just needs to be pointed at this URL.",
        });
        // refresh count
        const rc = await api.get("/sms/inbound?limit=1");
        setInboundCount(rc.data?.total ?? null);
      } else {
        toast.error(`Webhook returned ${r.status}`);
      }
    } catch (e) {
      toast.error("Self-test failed", { description: String(e) });
    } finally {
      setSelfTesting(false);
    }
  }

  return (
    <Card className="p-6 rounded-lg border border-zinc-200 shadow-none" data-testid="reminders-settings">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          <Bell className="h-3.5 w-3.5" /> SMS Appointment Reminders
        </div>
        {cfg?.updated_at && (
          <span className="text-2xs text-zinc-500 font-mono-num">
            Updated {fmtDateTime(cfg.updated_at)}
          </span>
        )}
      </div>

      <p className="mt-2 text-sm text-zinc-600 max-w-3xl">
        Auto-send SMS reminders before appointments. Clients can reply{" "}
        <span className="font-mono-num">Y</span> /{" "}
        <span className="font-mono-num">N</span> /{" "}
        <span className="font-mono-num">R</span> — replies are emailed to the admin, and
        an &ldquo;N&rdquo; reply cancels the appointment in Acuity (you can switch this off below).
      </p>

      {/* Slots */}
      <div className="mt-5 flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Switch checked={send48} onCheckedChange={setSend48} data-testid="reminder-send-48h" />
          Send 48h before
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Switch checked={send24} onCheckedChange={setSend24} data-testid="reminder-send-24h" />
          Send 24h before
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Switch checked={send2} onCheckedChange={setSend2} data-testid="reminder-send-2h" />
          Send 2h before
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Switch checked={autoCancel} onCheckedChange={setAutoCancel} data-testid="reminder-auto-cancel" />
          Auto-cancel appointment on &ldquo;N&rdquo; reply
        </label>
      </div>

      {/* Template body */}
      <div className="mt-5">
        <Label className="text-2xs uppercase tracking-wider text-zinc-500">Reminder template</Label>
        <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4}
          className="border-zinc-300 mt-1.5 font-mono-num text-sm" data-testid="reminder-template-body" />
        <p className="mt-1 text-2xs text-zinc-500">
          Merge fields: <span className="font-mono-num">{"{first_name}"}</span>{" "}
          <span className="font-mono-num">{"{name}"}</span>{" "}
          <span className="font-mono-num">{"{class_type}"}</span>{" "}
          <span className="font-mono-num">{"{class_time}"}</span>
        </p>
      </div>

      {/* Admin email */}
      <div className="mt-5">
        <Label className="text-2xs uppercase tracking-wider text-zinc-500">Admin email (notified on every reply)</Label>
        <Input value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)}
          placeholder="admin@example.com"
          className="border-zinc-300 mt-1.5 font-mono-num max-w-md" data-testid="reminder-admin-email" />
      </div>

      {/* Enabled types */}
      <div className="mt-5">
        <Label className="text-2xs uppercase tracking-wider text-zinc-500">
          Enable reminders for these appointment types ({enabledIds.size} selected)
        </Label>
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-64 overflow-y-auto rounded-md border border-zinc-200 p-2">
          {types.length === 0 ? (
            <div className="col-span-full text-sm text-zinc-500 py-4 text-center">No appointment types loaded.</div>
          ) : types.map((t) => (
            <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-zinc-50 px-2 py-1 rounded">
              <Checkbox
                checked={enabledIds.has(Number(t.id))}
                onCheckedChange={() => toggleType(Number(t.id))}
                data-testid={`reminder-type-${t.id}`}
              />
              <span className="truncate">{t.name}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Inbound webhook URL */}
      {webhookUrl && (
        <div className="mt-5 p-4 rounded-md bg-zinc-50 border border-zinc-200" data-testid="reminder-webhook-block">
          <div className="flex items-start justify-between gap-3">
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">
                ClickSend Inbound SMS Webhook
              </Label>
              <p className="mt-1 text-xs text-zinc-600 max-w-xl">
                Point ClickSend here so <b>every reply arrives in your Messaging inbox</b> instead of only as an email from ClickSend.
              </p>
            </div>
            {inboundCount === 0 && (
              <span
                className="shrink-0 text-2xs uppercase tracking-wider font-semibold bg-amber-100 text-amber-900 border border-amber-300 px-2 py-1 rounded"
                data-testid="reminder-inbound-zero-warning"
                title="No replies have ever arrived — the webhook is probably not configured in ClickSend."
              >
                0 replies received
              </span>
            )}
            {inboundCount != null && inboundCount > 0 && (
              <span className="shrink-0 text-2xs uppercase tracking-wider font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-1 rounded">
                {inboundCount} received
              </span>
            )}
          </div>

          <div className="mt-3 flex items-center gap-2">
            <Input value={webhookUrl} readOnly className="border-zinc-300 font-mono-num text-xs bg-white" data-testid="reminder-webhook-url" />
            <Button onClick={copyWebhook} variant="outline" className="border-zinc-300" data-testid="reminder-webhook-copy" title="Copy URL">
              {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            </Button>
            <Button
              onClick={sendSelfTest}
              disabled={selfTesting}
              variant="outline"
              className="border-zinc-300"
              data-testid="reminder-webhook-selftest"
              title="POSTs a fake reply to the URL — proves the endpoint is publicly reachable"
            >
              {selfTesting ? "Testing…" : "Self-test"}
            </Button>
          </div>

          <details className="mt-3">
            <summary className="cursor-pointer text-xs font-semibold text-zinc-700 select-none">
              Where to paste this in ClickSend →
            </summary>
            <ol className="mt-2 pl-5 text-xs text-zinc-700 leading-relaxed list-decimal space-y-1">
              <li>Log in to <a href="https://dashboard.clicksend.com" target="_blank" rel="noreferrer" className="underline">dashboard.clicksend.com</a>.</li>
              <li>Left sidebar → <b>SMS</b> → <b>Inbound Settings</b> (some ClickSend UIs call this &ldquo;SMS → Settings → Inbound Rules&rdquo;).</li>
              <li>Click <b>Add Rule</b> (or edit the default rule for your dedicated number / alpha sender).</li>
              <li>Set <b>Action</b> to <code className="text-2xs bg-zinc-100 px-1 rounded">URL / Webhook (POST)</code>.</li>
              <li>Paste the URL above into the <b>URL</b> field.</li>
              <li>Choose <b>Content-Type: application/json</b>. Method: <b>POST</b>.</li>
              <li>Save. Reply to one of your own outbound SMS from a phone — the reply will land in the Messaging inbox within a few seconds.</li>
            </ol>
            <p className="mt-2 text-2xs text-zinc-500">
              Tip: click <b>Self-test</b> above to POST a fake reply to your own endpoint. If a row appears in the Messaging inbox labelled <code>SELF-TEST&nbsp;…</code>, the endpoint is reachable and only ClickSend&apos;s config remains.
            </p>
          </details>
        </div>
      )}

      <div className="mt-5">
        <Button onClick={save} disabled={saving} className="bg-zinc-950 hover:bg-zinc-800 text-white" data-testid="reminders-save-btn">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save reminder settings"}
        </Button>
      </div>

      <OptOutsPanel />
    </Card>
  );
}

// ============================================================================
// OPT-OUTS — small admin list for STOP/UNSUBSCRIBE recipients
// ============================================================================
function OptOutsPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await api.get("/sms/opt-outs");
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      // soft-fail; the rest of the page still works
    }
  }
  useEffect(() => { load(); }, []);

  async function addOptOut() {
    if (!phone.trim()) return;
    setBusy(true);
    try {
      await api.post("/sms/opt-outs", { phone: phone.trim() });
      toast.success("Added to opt-out list");
      setPhone("");
      await load();
    } catch (e) {
      toast.error("Could not add", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  }

  async function removeOptOut(p) {
    if (!confirm(`Opt ${p} back in?`)) return;
    try {
      await api.delete(`/sms/opt-outs/${encodeURIComponent(p)}`);
      toast.success("Opted back in");
      await load();
    } catch (e) {
      toast.error("Could not remove", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <div className="mt-7 pt-5 border-t border-zinc-200" data-testid="opt-outs-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          SMS Opt-outs · {total}
        </div>
        <p className="text-2xs text-zinc-500">
          Replies of <span className="font-mono-num">STOP / UNSUBSCRIBE / QUIT</span> auto-add a number here.
          <span className="font-mono-num"> START</span> opts them back in.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 items-center mb-3">
        <Input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="+614…  or  04XX XXX XXX"
          className="border-zinc-300 font-mono-num max-w-xs"
          data-testid="optout-phone-input"
        />
        <Button onClick={addOptOut} disabled={busy || !phone.trim()}
          variant="outline" className="border-zinc-300" data-testid="optout-add-btn">
          Add opt-out
        </Button>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500 py-3">No numbers on the opt-out list.</p>
      ) : (
        <div className="max-h-56 overflow-y-auto rounded-md border border-zinc-200 divide-y divide-zinc-100">
          {rows.map((r) => (
            <div key={r.phone} className="p-2 flex items-center gap-2 text-sm">
              <div className="font-mono-num">{r.phone}</div>
              <div className="text-2xs text-zinc-500">
                {r.source === "inbound_keyword" ? "via SMS reply" : r.source}
                {r.opted_out_at ? " · " + fmtDateTime(r.opted_out_at) : ""}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="ml-auto border-zinc-300 h-7 px-2 text-xs"
                onClick={() => removeOptOut(r.phone)}
                data-testid={`optout-remove-${r.phone}`}
              >
                Opt back in
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
